import React, { useRef, useState } from 'react'

import { DataGrid } from '@the-platform-group/component-library/DataGrid'
import { getColumnValues, getRowValues } from './CustomerTableValues'

import { useSessionState } from 'contexts/Session'
import useCustomerAuthorization from 'components/CustomerActionsDropdown/hooks/useCustomerAuthorization'
import { useQueryClient } from '@tanstack/react-query'
import CustomerLocation from 'pages/CustomerAccount/components/CustomerLocation'
import CustomerSublocations from 'pages/CustomerAccount/components/CustomerSublocations'
import { CUSTOMER_ACCOUNT_QUERY_KEY } from 'pages/Accounts/hooks/useCustomers'

function CustomersTable({
  isLoading,
  customers,
  pagination,
  setPagination,
  handleCustomerEdit,
  handleCustomerFeatureToggle,
  handleCustomerArchive,
  handleCustomerUnarchive,
  handleCustomerSecurity,
}) {
  const { currentUser } = useSessionState()
  const { setupAuthorizeCustomerMutation } = useCustomerAuthorization()
  const queryClient = useQueryClient()
  const [managingCustomerLocation, setManagingCustomerLocation] = useState(null)
  const [managingCustomerSubLocation, setManagingCustomerSubLocation] = useState(null)
  const sublocationsChangedRef = useRef(false)

  setupAuthorizeCustomerMutation()

  const { page, itemCount = 0, pageSize } = pagination

  const handlePageChange = newPage => {
    setPagination(prevState => ({
      ...prevState,
      page: newPage,
      pageOffset: newPage * pagination.pageSize,
    }))
  }

  const handleRowsChange = newPageSize => {
    setPagination(prevState => ({ ...prevState, page: 0, pageSize: newPageSize, pageOffset: 0 }))
  }

  const handleLocationMutation = () =>
    queryClient.invalidateQueries({ queryKey: [CUSTOMER_ACCOUNT_QUERY_KEY] })

  const handlers = {
    handleCustomerEdit,
    handleCustomerFeatureToggle,
    handleCustomerArchive,
    handleCustomerUnarchive,
    handleCustomerSecurity,
    handleManageLocation: setManagingCustomerLocation,
    handleManageSublocations: setManagingCustomerSubLocation,
  }

  return (
    <>
      <DataGrid
        name="customerAccounts"
        columns={getColumnValues()}
        commonColumnProps={{ headerAlign: 'left' }}
        getRowHeight={() => 'auto'} // set row height to auto since this table doesn't have density selector
        rows={getRowValues(customers, currentUser.customerAuthorizations, handlers)}
        sx={{
          '.MuiDataGrid-actionsCell .MuiIconButton-root': { px: 1 },
          '.hoverCell': {
            '&.MuiDataGrid-cell:hover span, &.MuiDataGrid-cell:hover .MuiButtonBase-root': {
              display: 'block',
            },
            'span, .MuiButtonBase-root': { display: 'none' },
          },
        }}
        loading={isLoading}
        localeText={{
          noRowsLabel:
            'Looks like nothing matches your search. Try removing some criteria or attempt a new search.',
        }}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handleRowsChange}
        rowCount={itemCount}
        pageSizeOptions={[10, 25, 50]}
      />
      {managingCustomerLocation && (
        <CustomerLocation
          open
          onClose={() => setManagingCustomerLocation(null)}
          customerId={managingCustomerLocation.id}
          existingLocation={managingCustomerLocation.customerLocation || null}
          onSaveLocationSuccess={handleLocationMutation}
          onRemoveLocationSuccess={handleLocationMutation}
        />
      )}
      {managingCustomerSubLocation && (
        <CustomerSublocations
          open
          onClose={() => {
            if (sublocationsChangedRef.current) {
              queryClient.invalidateQueries({ queryKey: [CUSTOMER_ACCOUNT_QUERY_KEY] })
              sublocationsChangedRef.current = false
            }
            setManagingCustomerSubLocation(null)
          }}
          customerLocationId={managingCustomerSubLocation.id}
          initialSublocations={managingCustomerSubLocation.customerSublocations || []}
          onSublocationsChange={() => {
            sublocationsChangedRef.current = true
          }}
        />
      )}
    </>
  )
}

export default CustomersTable
